// 事件詳細「前後片段」的防呆（VMS UX 評審第五輪 B5-01、R5-P2-7；SPEC #vms-web-478「事件詳細的前後片段」）：
// 雲端片段的長度跟事件對不上（dev 看到 lstw-16ch / CH2 的一段寫 9:51:29、畫面大半是綠色）時，
// 不顯示片段自己的長度、只播事件前後那一段，並寫「片段可能不完整」。根本原因在錄影端（後端查），這裡只防呆。

/** 單一片段超過這個長度就當異常（雲端事件片段通常幾十秒；全時錄影的一段也不會到半小時） */
export const CLIP_FRAG_MAX_SEC = 30 * 60;
/** 整份播放清單超過這個長度就當異常（事件前後片段只要 30 秒） */
export const CLIP_TOTAL_MAX_SEC = 2 * 3600;

export interface FragInfo {
  duration: number;
}

/** 片段長度異常？ */
export function clipSuspect(frags: FragInfo[], totalSec: number): boolean {
  if (!Number.isFinite(totalSec) || totalSec > CLIP_TOTAL_MAX_SEC) return true;
  return frags.some((f) => !Number.isFinite(f.duration) || f.duration > CLIP_FRAG_MAX_SEC);
}

// ---- 回放頁、多路同步、同一時刻也用同一個判斷（VMS UX 評審第六輪 R6-P0-1、B6-01；SPEC #vms-web-459「雲端片段長度防呆」） ----
// dev 上一段舊壞片段（lstw-16ch / CH2，12:01 起、長 35489 秒）在時間軸畫到 21:52（超過現在），蓋住之後的新片段；
// 從事件來預設選雲端 → 綠畫面，而且 readyState 4，8 秒逾時回退不會觸發。所以在「片段索引」這一層就先擋：
//   - 一段雲端片段 > CLIP_FRAG_MAX_SEC，或結束時間超過「現在」（容許 60 秒時鐘誤差）→ 算可疑
//   - 可疑片段的結束截到 min(現在, 下一段片段的開始, 開始 + CLIP_FRAG_MAX_SEC)：時間軸不畫未來、不蓋住其他片段
//   - 可疑的時段另外回傳，畫面用來寫「這段雲端片段可能不完整」並優先改選同一刻有錄影的本地來源

/** 結束時間比「現在」晚多少秒以內算時鐘誤差、不算可疑 */
export const CLIP_FUTURE_SKEW_SEC = 60;

export interface ClipRange {
  start: number;
  end: number;
}

export interface GuardedClips<T extends ClipRange> {
  clips: T[];
  /** 可疑片段（已截過）的 [start, end) */
  suspect: ClipRange[];
}

/** 一段雲端片段（秒）是否可疑：太長、或結束在未來。 */
export function clipRangeSuspect(c: ClipRange, now: number): boolean {
  const d = c.end - c.start;
  return !Number.isFinite(d) || d > CLIP_FRAG_MAX_SEC || c.end > now + CLIP_FUTURE_SKEW_SEC;
}

/** 截掉可疑片段：結束 = min(原本結束, 現在, 下一段的開始, 開始 + 30 分鐘)；截完沒有長度的整段拿掉。 */
export function guardClips<T extends ClipRange>(clips: T[], now: number): GuardedClips<T> {
  const sorted = [...clips].sort((a, b) => a.start - b.start);
  const out: T[] = [];
  const suspect: ClipRange[] = [];
  sorted.forEach((c, i) => {
    if (!clipRangeSuspect(c, now)) {
      out.push(c);
      return;
    }
    const next = sorted.slice(i + 1).find((x) => x.start > c.start)?.start ?? Infinity;
    const end = Math.min(Number.isFinite(c.end) ? c.end : Infinity, now, next, c.start + CLIP_FRAG_MAX_SEC);
    if (!(end > c.start)) return;
    out.push({ ...c, end });
    suspect.push({ start: c.start, end });
  });
  return { clips: out, suspect };
}

/** at 落在可疑時段裡？ */
export function inSuspect(suspect: ClipRange[] | undefined, at: number): boolean {
  return !!suspect?.some((s) => at >= s.start && at < s.end);
}

/** hls.js 的播放清單（PROGRAM-DATE-TIME 是 ms）裡有沒有可疑片段：太長、或照 PROGRAM-DATE-TIME 算結束在未來。 */
export function fragsSuspect(frags: { duration: number; programDateTime?: number | null }[], now: number): boolean {
  return frags.some((f) => {
    if (!Number.isFinite(f.duration) || f.duration > CLIP_FRAG_MAX_SEC) return true;
    return typeof f.programDateTime === 'number' && f.programDateTime / 1000 + f.duration > now + CLIP_FUTURE_SKEW_SEC;
  });
}
